import {
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  FilterChangedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  SideBarDef,
} from 'ag-grid-community';
import { Component, OnInit } from '@angular/core';

import { TaskType } from '@compumark/analyst-selection-tool-client';
import {
  FilterChip,
  TextFilterComponent,
} from '@compumark/brand-context-components';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { first } from 'rxjs';
import { MaterialTooltipComponent } from 'src/app/data-grid/components/material-tooltip/material-tooltip.component';
import { LoadingComponent } from 'src/app/results/loading/loading.component';
import { DateFormatterService } from 'src/app/results/services/date-formatter.service';
import { FilterParams } from 'src/app/results/services/filters.params';
import { CellRendererService } from '../../../results/services/cell-renderer.service';
import { SortStateService } from '../../../results/services/sort-state.service';
import { TableColumnService } from '../../../results/services/table-column-service';
import { UserService } from '../../../security/services/user.service';
import { DetailsComponent } from '../details/details.component';
import { TrademarkFilterComponent } from '../filter/trademark-filter/trademark-filter.component';
import { HoldCellRendererComponent } from '../hold-cell-renderer/hold-cell-renderer.component';
import { PreviewLogoRendererComponent } from '../preview-logo-renderer/preview-logo-renderer.component';
import { QualityIconRendererComponent } from '../quality-icon-renderer/quality-icon-renderer.component';
import { ResultsLinkRendererComponent } from '../results-link-renderer/results-link-renderer.component';
import { BrowserDetectionService } from '../services/browser-detection.service';
import { TaskFilterChangeService } from '../services/task-filter-change.service';
import { TasksOverviewDatasource } from '../services/tasks-overview-datasource.service';
import { TimeLeftRendererComponent } from '../time-left-renderer/time-left-renderer.component';

@UntilDestroy()
@Component({
  selector: 'app-tasks-overview',
  standalone: false,
  templateUrl: './tasks-overview.component.html',
  styleUrls: ['./tasks-overview.component.scss'],
})
export class TasksOverviewComponent implements OnInit {
  static readonly filtervalues = 'filterValues_';
  gridApi?: GridApi;

  public sideBar: SideBarDef = {
    toolPanels: [TableColumnService.columnsOpenPanel],
    position: 'right',
    hiddenByDefault: false,
  };
  protected lockEnableFields: string[] = [];

  constructor(
    private tasksOverviewDatasource: TasksOverviewDatasource,
    private filterChangeService: TaskFilterChangeService,
    private userService: UserService,
    private tableColumnService: TableColumnService,
    private cellRenderer: CellRendererService,
    private browserDetectionService: BrowserDetectionService,
    private sortStateService: SortStateService,
    private dateFormatter: DateFormatterService,
  ) {}

  readonly gridOptions: GridOptions = {
    suppressHeaderFocus: true,
    columnDefs: this.getColumnDefs(),
    rowModelType: 'serverSide',
    suppressColumnVirtualisation: true,
    blockLoadDebounceMillis: 1000,
    detailCellRenderer: DetailsComponent,
    masterDetail: true,
    detailRowAutoHeight: true,
    enableCellTextSelection: true,
    suppressDragLeaveHidesColumns: true,
    loadingCellRenderer: 'loadingComponent',
    onFilterChanged: (event: FilterChangedEvent) => {
      this.filterChangeService.onFilterChange(event);
      localStorage.setItem(
        `filterValues_${this.getCurrentUserName()}`,
        JSON.stringify(event.api.getFilterModel()),
      );

      this.filterChangeService.isGridInitializingObs.next(false);
    },
    onSortChanged: /* istanbul ignore next */ () => {
      const key = this.generateSortKey();
      this.sortStateService.saveSortState(this.gridApi!, key);
    },
    onGridReady: (event: GridReadyEvent) => {
      this.gridApi = event.api;
      this.filterChangeService.isGridInitializingObs.next(true);
      this.userService
        .getCurrentUserObservable()
        .pipe(untilDestroyed(this))
        .subscribe((user) => {
          event.api.setGridOption(
            'serverSideDatasource',
            this.tasksOverviewDatasource.createFor(),
          );

          const storedFilters = localStorage.getItem(
            `filterValues_${user.name}`,
          );
          if (storedFilters) {
            const filterModel = JSON.parse(storedFilters);
            event.api.setFilterModel(filterModel);
            event.api.onFilterChanged();
          }
          this.filterChangeService.initializeLockedFilterChips(user!);

          this.initSorting();

          this.tableColumnService.initColumns(
            this.gridApi!,
            user,
            'taskOverviewTableColumns',
          );
          this.lockEnableFields =
            this.filterChangeService.getLockEnabledFields();
        });
    },
    components: {
      textColumnFilter: TextFilterComponent,
      materialTooltip: MaterialTooltipComponent,
      loadingComponent: LoadingComponent,
      trademarkFilterComponent: TrademarkFilterComponent,
      previewLogoRendererComponent: PreviewLogoRendererComponent,
    },

    defaultColDef: {
      sortable: false,
    },
    onColumnResized: (event: ColumnResizedEvent): void => {
      if (event.column !== null) {
        this.tableColumnService.onColumnChange(event);
      }
    },
    onColumnMoved: (event: ColumnMovedEvent) => {
      this.tableColumnService.onColumnChange(event);
    },
    onColumnVisible: (event: ColumnVisibleEvent) => {
      this.tableColumnService.onColumnChange(event);
    },
  };

  private initSorting(): void {
    this.sortStateService.restoreSortState(
      this.gridApi!,
      this.generateSortKey(),
    );
    this.gridApi!.onSortChanged();
  }

  private getCurrentUserName(): string | undefined {
    const userName = this.userService.getCurrentUser();
    return userName?.name;
  }

  private getColumnDefs(): ColDef[] {
    return [
      {
        field: '',
        cellRendererSelector: (params) => {
          if (!!params.data.productionOrderIdentifier) {
            return { component: 'agGroupCellRenderer' };
          }
          return undefined;
        },
        valueGetter: () => '',
        width: 60,
        resizable: true,
      },
      {
        colId: 'holdStatus',
        headerName: 'Hold',
        field: 'holdStatus',
        cellRenderer: HoldCellRendererComponent,
        cellStyle: {
          padding: 0,
        },
        width: 120,
        resizable: true,
        menuTabs: [],
      },
      {
        headerName: 'Preview',
        cellRendererSelector: (params) => {
          if (
            params.data.taskType === TaskType.SELECTION &&
            params.data.orderType === 'OPSI'
          ) {
            return { component: 'previewLogoRendererComponent' };
          }
          return undefined;
        },
        resizable: true,
        menuTabs: [],
      },
      {
        headerName: 'Order ID',
        field: 'orderId',
        tooltipField: 'orderId',
        tooltipComponent: 'materialTooltip',
        cellRenderer: ResultsLinkRendererComponent,
        filter: 'textColumnFilter',
        menuTabs: ['filterMenuTab'],
        width: 150,
        resizable: true,
      },
      {
        headerName: 'Search ID',
        field: 'searchId',
        tooltipField: 'search ID',
        tooltipComponent: 'materialTooltip',
        filter: 'textColumnFilter',
        menuTabs: ['filterMenuTab'],
        width: 150,
        resizable: true,
        sortable: true,
        cellClass: (params) => {
          if (
            ResultsLinkRendererComponent.resultsDeliveryFailedStatuses.includes(
              params.data.resultsDeliveryStatus,
            )
          ) {
            return 'error';
          }
          return null;
        },
      },
      {
        headerName: 'Type',
        field: 'orderType',
        tooltipField: 'orderType',
        tooltipComponent: 'materialTooltip',
        filter: 'agSetColumnFilter',
        menuTabs: ['filterMenuTab'],
        filterParams: {
          defaultToNothingSelected: true,
          ...FilterParams.setFilterParams,
          values: ['BRAND_LANDSCAPE_ANALYZER', 'OPSI', 'ARCHIVED'],
        },
        width: 150,
        resizable: true,
      },
      {
        headerName: 'Trademark',
        field: 'trademark',
        tooltipField: 'trademark',
        tooltipComponent: 'materialTooltip',
        filter: 'trademarkFilterComponent',
        menuTabs: ['filterMenuTab'],
        resizable: true,
        sortable: true,
      },
      {
        headerName: 'Product',
        field: 'productDescription',
        tooltipField: 'productDescription',
        tooltipComponent: 'materialTooltip',
        filter: 'agSetColumnFilter',
        menuTabs: ['filterMenuTab'],
        filterParams: {
          defaultToNothingSelected: true,
          ...FilterParams.setFilterParams,
          values: (params: any) => {
            this.filterChangeService
              .getProductFilterValues()
              .pipe(first())
              .subscribe((res) => {
                params.success(res);
              });
          },
          refreshValuesOnOpen: true,
        },
        resizable: true,
      },
      {
        headerName: 'Brand',
        field: 'brand',
        tooltipField: 'Brand',
        tooltipComponent: 'materialTooltip',
        filter: 'textColumnFilter',
        menuTabs: ['filterMenuTab'],
        width: 150,
        resizable: true,
      },
      {
        headerName: 'Task',
        field: 'taskDescription',
        tooltipField: 'taskDescription',
        tooltipComponent: 'materialTooltip',
        filter: 'agSetColumnFilter',
        menuTabs: ['filterMenuTab'],
        filterParams: {
          defaultToNothingSelected: true,
          ...FilterParams.setFilterParams,
          values: (params: any) => {
            this.filterChangeService
              .getTaskFilterValues()
              .pipe(first())
              .subscribe((res) => {
                params.success(res);
              });
          },
          refreshValuesOnOpen: true,
        },
        width: 150,
        resizable: true,
      },
      {
        headerName: 'Task Status',
        field: 'taskStatus.label',
        tooltipField: 'taskStatus.label',
        tooltipComponent: 'materialTooltip',
        filter: 'agSetColumnFilter',
        menuTabs: ['filterMenuTab'],
        filterParams: {
          defaultToNothingSelected: true,
          suppressSorting: true,
          ...FilterParams.setFilterParams,
          values: [
            'ToDo',
            'ToDo (Maybe)',
            'Waiting',
            'Busy',
            'Finished',
            'Skipped (Finished)',
            'Cancelled',
          ],
        },
        width: 150,
        resizable: true,
      },
      {
        headerName: 'Analyst',
        field: 'analystName',
        tooltipField: 'analystName',
        tooltipComponent: 'materialTooltip',
        filter: 'textColumnFilter',
        menuTabs: ['filterMenuTab'],
        width: 150,
        resizable: true,
        sortable: true,
      },
      {
        headerName: 'Contributors',
        field: 'contributors',
        minWidth: 150,
        resizable: true,
        menuTabs: [],
      },
      {
        headerName: 'Delivery date',
        field: 'deliveryDate',
        valueGetter: (params) =>
          this.dateFormatter.formatDateTime(params.data.deliveryDate),
        tooltipValueGetter: (params) =>
          this.dateFormatter.formatDateTime(params.data.deliveryDate),
        tooltipComponent: 'materialTooltip',
        filter: 'agDateColumnFilter',
        menuTabs: ['filterMenuTab'],
        filterParams: FilterParams.dateFilterParams,
        width: 150,
        resizable: true,
        sortable: true,
      },
      {
        headerName: 'Time left',
        cellRenderer: TimeLeftRendererComponent,
        width: 150,
        autoHeight: true,
        wrapText: true,
        resizable: true,
        menuTabs: [],
      },
      {
        headerName: 'Firm',
        field: 'firm',
        tooltipField: 'firm',
        tooltipComponent: 'materialTooltip',
        filter: 'textColumnFilter',
        menuTabs: ['filterMenuTab'],
        width: 150,
        resizable: true,
      },
      {
        headerName: 'Statistics',
        field: 'statistics',
        cellRenderer: (params: any) => {
          return this.cellRenderer.getStatistics(params);
        },
        width: 150,
        autoHeight: true,
        resizable: true,
        menuTabs: [],
      },
      {
        headerName: 'Quality',
        field: 'qualityRequestOrigin',
        cellRenderer: QualityIconRendererComponent,
        minWidth: 145,
        resizable: true,
        menuTabs: [],
      },
      {
        headerName: 'Keywords',
        field: 'keywords',
        minWidth: 145,
        resizable: true,
        menuTabs: [],
      },
    ];
  }

  ngOnInit(): void {
    if (!this.browserDetectionService.isChrome()) {
      alert(
        'For the best user experience, please use Google Chrome to access the Analyst Selection Tool. Chrome ensures optimal performance and compatibility.',
      );
    }
  }

  removeFilter(filterChip: FilterChip): void {
    const filterModel = this.gridApi!.getFilterModel();
    const filter = filterModel[filterChip.field];

    if (filter?.filterType === 'set') {
      const newValues = filter.values.filter(
        (value: string) => value !== filterChip.value,
      );
      if (newValues.length) {
        filterModel[filterChip.field].values = newValues;
      } else {
        delete filterModel[filterChip.field];
      }
    } else {
      delete filterModel[filterChip.field];
    }

    this.filterChangeService.clearChip(filterChip);
    this.gridApi?.setFilterModel(filterModel);
    this.gridApi?.onFilterChanged();
  }

  removeAllFilters(): void {
    this.filterChangeService.removeAllFilters(this.gridApi!);
  }

  lockFilterChip(filterChip: FilterChip): void {
    this.filterChangeService.toggleLockState(filterChip);
  }

  generateSortKey(): string {
    return `sortValues_${this.getCurrentUserName()}`;
  }
}
